import { type ChangeEvent, memo, useCallback, useId } from 'react';

import { cn, tailwindVariants } from '@air/utils-tailwind';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import { WORKSPACE_AGENT_PANEL_GRAYSCALE_PREVIEW } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import {
  isWorkspaceAgentGrayscaleFilterId,
  WORKSPACE_AGENT_GRAYSCALE_FILTERS,
  type WorkspaceAgentGrayscaleFilterId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGrayscale';

const grayscaleFilterDot = tailwindVariants({
  base: 'size-2.5 shrink-0 rounded-full',
  variants: {
    filter: {
      meadow: 'bg-green-9',
      neutral: 'bg-gray-9',
      silk: 'bg-orange-9',
      velvet: 'bg-red-9',
    },
  },
});

export type WorkspaceAgentGuidedVariantGrayscaleSettingsProps = {
  onFilterChange: (filterId: WorkspaceAgentGrayscaleFilterId) => void;
  previewImageUrl: string | null;
  value: WorkspaceAgentGrayscaleFilterId;
};

export const WorkspaceAgentGuidedVariantGrayscaleSettings = memo(
  ({ onFilterChange, previewImageUrl, value }: WorkspaceAgentGuidedVariantGrayscaleSettingsProps) => {
    const filterGroupName = useId();
    const previewFilterPrefix = useId().replaceAll(':', '');

    const handleFilterChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const filterId = event.currentTarget.value;

        if (isWorkspaceAgentGrayscaleFilterId(filterId)) {
          onFilterChange(filterId);
        }
      },
      [onFilterChange],
    );

    return (
      <div className="flex items-start gap-3">
        <svg aria-hidden className="absolute size-0">
          {WORKSPACE_AGENT_GRAYSCALE_FILTERS.map((filter) => (
            <filter colorInterpolationFilters="sRGB" id={`${previewFilterPrefix}-${filter.id}`} key={filter.id}>
              <feColorMatrix type="matrix" values={filter.previewColorMatrix} />
            </filter>
          ))}
        </svg>
        <div
          className="size-20 shrink-0 overflow-hidden rounded-8 bg-gray-4"
          data-filter-id={value}
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_GRAYSCALE_PREVIEW"
          style={{ filter: `url(#${previewFilterPrefix}-${value})` }}
        >
          {previewImageUrl ? (
            <WorkspaceAgentPanelImage
              {...WORKSPACE_AGENT_PANEL_GRAYSCALE_PREVIEW}
              alt={`${value} grayscale preview`}
              className="size-full object-cover"
              data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_GRAYSCALE_PREVIEW_IMAGE"
              src={previewImageUrl}
            />
          ) : null}
        </div>

        <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
          <legend className="font-medium text-12 text-gray-12">Filter</legend>
          <div className="flex max-w-[200px] flex-wrap gap-2">
            {WORKSPACE_AGENT_GRAYSCALE_FILTERS.map((filter) => {
              const isSelected = filter.id === value;

              return (
                <label
                  className={cn(
                    'relative flex w-fit cursor-pointer items-center gap-1.5 rounded-full border bg-gray-1 px-2.5 py-1 text-12 has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-8',
                    isSelected ? 'border-blue-8 text-blue-11' : 'border-gray-5 text-gray-12 hover:border-gray-6',
                  )}
                  key={filter.id}
                >
                  <input
                    checked={isSelected}
                    className="absolute inset-0 cursor-pointer opacity-0"
                    data-filter-id={filter.id}
                    data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_GRAYSCALE_FILTER"
                    name={filterGroupName}
                    onChange={handleFilterChange}
                    type="radio"
                    value={filter.id}
                  />
                  <span aria-hidden className={grayscaleFilterDot({ filter: filter.id })} />
                  {filter.label}
                </label>
              );
            })}
          </div>
        </fieldset>
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantGrayscaleSettings.displayName = 'WorkspaceAgentGuidedVariantGrayscaleSettings';
